import { Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import LoopIcon from '@mui/icons-material/Loop';
import { useEffect, useMemo, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import ProgressBar from '../../../../../reusable-components/progress-bar/ProgressBar';
import CloseIconButton from '../../../../../reusable-components/basic-buttons/CloseIconButton';
import { useSelector } from '../../../../../redux/store';
import TicketTypeFields from './TicketTypeFields';
import TicketEditor from './TicketEditor';
import SubjectInput from './SubjectInput';
import AddUsersToTicket from './AddUsersToTicket';
import { CustomAvatar } from '../../../../../reusable-components/custom-avatar';
import TicketFacCatTypeSelect from './TicketFacCatTypeSelect';
import LoadingAnimation from '../../../../../reusable-components/loader/LoadingAnimation';
import { changeActiveGroup, getHeaderGroups, getPossibleUsers, getTicketMessageAutoSignature, setInitialTicketHtmlHelper, setTicketTypeFieldGroupsHelper, submitTicket, ticketMessageSubmitErrorMessage } from './TicketHelper';

NewTicketForm.propTypes = {
  isTestTicket: PropTypes.bool,
  setOpen: PropTypes.func,
  facilities: PropTypes.array,
  users: PropTypes.array,
  ticketTypes: PropTypes.array,
  ticketCategories: PropTypes.array,
  facilityCategories: PropTypes.array,
  facilityTypes: PropTypes.object,
  facilityId: PropTypes.number,
  ticketDataLoaded: PropTypes.bool,
};

export default function NewTicketForm({
  isTestTicket,
  setOpen,
  facilities,
  users,
  ticketTypes,
  ticketCategories,
  facilityCategories,
  facilityTypes,
  facilityId,
  ticketDataLoaded
}) {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [ticketTypeFieldGroups, setTicketTypeFieldGroups] = useState([]);
  const [ticketHtml, setTicketHtml] = useState(null);
  const [subject, setSubject] = useState('');
  const [openTicketMessageEditer, setOpenTicketMessageEditer] = useState(false);
  const [confirmedCategoryFacilityAndType, setConfirmedCategoryFacilityAndType] = useState(false);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [newUsers, setNewUsers] = useState([...users]);
  const [attachments, setAttachments] = useState([]);
  const [possibleUsers, setPossibleUsers] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);

  const categoryFacilityAndTypeSet = ticketDataLoaded && (selectedFacility || facilities.length === 0) && (selectedTicketType || filteredTypes.length === 0) &&
    (selectedCategory || filteredCategories.length === 0);
  const autoSignature = getTicketMessageAutoSignature(user);

  const handleClose = () => {
    setOpen(false);
    clearData();
  };
 
  const handleDelete = (participantId) => setNewUsers(newUsers.filter((user) => user.id !== participantId));

  const clearData = () => {
    if (ticketDataLoaded && facilities.length === 0 && ticketTypes.length === 0 && ticketCategories.length === 0) {
      setInitialTicketHtmlHelper(setOpenTicketMessageEditer, selectedTicketType, ticketTypeFieldGroups, setSubject, setNewUsers, setTicketHtml, user, true);
      return;
    }
    setSubject('');
    setTicketHtml(null);
    setOpenTicketMessageEditer(false);
    setSelectedFacility(facilityId ? facilities?.length ? facilities.find((facility) => facility.id === facilityId) : { id: facilityId, label: '' } : null);
    setSelectedCategory(null);
    setConfirmedCategoryFacilityAndType(false);
    if (!isTestTicket) {
      setTicketTypeFieldGroups([]);
      setSelectedTicketType(null);
    } else {
      const testType = ticketTypes.find(type => type.value === -1);
      setSelectedTicketType(testType);
      setTicketTypeFieldGroupsHelper(testType, setTicketTypeFieldGroups, false, null);
    }
  };

  useEffect(() => {
    getPossibleUsers(setPossibleUsers, confirmedCategoryFacilityAndType, selectedFacility, selectedCategory, selectedTicketType);  
  }, [confirmedCategoryFacilityAndType, selectedFacility]);

  useEffect(() => {   
    setTicketTypeFieldGroupsHelper(selectedTicketType, setTicketTypeFieldGroups, confirmedCategoryFacilityAndType,
      () => setInitialTicketHtmlHelper(setOpenTicketMessageEditer, selectedTicketType, ticketTypeFieldGroups, setSubject, setNewUsers, setTicketHtml, user, true));      
  }, [selectedTicketType, confirmedCategoryFacilityAndType]);

  useEffect(() => {
    if (isTestTicket && ticketTypes?.length) setSelectedTicketType(type => type?.value === -1 ? ticketTypes.find(ty => ty.value === -1) : type);
  }, [isTestTicket, ticketTypes]);

  const headerGroups = useMemo(() => getHeaderGroups(ticketTypeFieldGroups), [ticketTypeFieldGroups]); 

  return (
    <>
      <DialogTitle sx={{ textAlign: 'center', p: 3 }}>
        New Ticket
        <Tooltip title="Reset Ticket">
          <IconButton size="small" color={'secondary'} sx={{ ml: 1 }} onClick={clearData}>
            <LoopIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ m: 2 }}>
          <ProgressBar values={[100, confirmedCategoryFacilityAndType ? 100 : 0, ...headerGroups, ticketHtml ? 100 : 0]} />
        </Box>
        {setOpen && <CloseIconButton onClick={handleClose} />}
      </DialogTitle>

      <DialogContent>
        {!ticketDataLoaded && <LoadingAnimation />}
        {!confirmedCategoryFacilityAndType && ticketDataLoaded && <TicketFacCatTypeSelect
          selectedCategory={selectedCategory}
          selectedFacility={selectedFacility}
          selectedTicketType={selectedTicketType}
          setSelectedCategory={setSelectedCategory}
          setSelectedTicketType={setSelectedTicketType}
          setSelectedFacility={setSelectedFacility}
          ticketTypes={ticketTypes}
          facilities={facilities}
          ticketCategories={ticketCategories}
          facilityCategories={facilityCategories}
          facilityTypes={facilityTypes}
          facilityId={facilityId}
          isTestTicket={isTestTicket}
          filteredCategories={filteredCategories}
          setFilteredCategories={setFilteredCategories}
          filteredTypes={filteredTypes}
          setFilteredTypes={setFilteredTypes}
          setConfirmedCategoryFacilityAndType={setConfirmedCategoryFacilityAndType}
        />}
        {!openTicketMessageEditer && confirmedCategoryFacilityAndType &&
          <TicketTypeFields
            ticketTypeFieldGroups={ticketTypeFieldGroups.filter(group => group.active)}
            setTicketTypeFieldGroups={setTicketTypeFieldGroups}
            facility={selectedFacility}
          />
        }
        {openTicketMessageEditer && confirmedCategoryFacilityAndType && <>
          <SubjectInput value={subject} setValue={setSubject} />
          {possibleUsers !== null ? <TicketEditor
            value={ticketHtml}
            onChange={setTicketHtml}
            attachments={attachments}
            setAttachments={setAttachments}
            users={possibleUsers}
            editorProps={{
              attachFile: true,
              id: "new-item-ticket",
              placeholder: selectedTicketType ? `${selectedTicketType.label} ticket...` : ""
            }}
          /> : <Box sx={{ textAlign: "center" }}><CircularProgress /></Box>}
        </>}
        {ticketHtml && <Grid container direction="row" justifyContent="flex-start" alignItems="center" sx={{ mt: 2 }}>
          <Typography mr={.5}>Recipients:</Typography>
          {newUsers?.map((participant, index) => <Chip
            key={participant.id}
            label={participant.value || participant?.id}
            onDelete={() => { if (user.email !== participant.id) handleDelete(participant.id); }}
            deleteIcon={<CancelIcon sx={user.email === participant.id ? { pointerEvents: 'none', opacity: 0.5 } : {}} />}
            avatar={<CustomAvatar
              name={participant.value || participant?.id}
              email={participant.value && participant?.id}
              index={index}
              key={index}
              sx={{ '&.MuiChip-avatar': { color: '#fff' } }}
            />}
            sx={{ mr: 1, maxWidth: "180px" }}
          />)}
          <AddUsersToTicket newUsers={newUsers} setNewUsers={setNewUsers} possibleUsers={possibleUsers} />
        </Grid>}
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "center", zIndex: 1, p: 3 }}>
        {!confirmedCategoryFacilityAndType && <Button
          variant="contained"
          disabled={!categoryFacilityAndTypeSet}
          onClick={() => setConfirmedCategoryFacilityAndType(true)}
          sx={{ width: '50%' }}
        >
          Next
        </Button>}
        {confirmedCategoryFacilityAndType && !ticketHtml && <Button
          variant="contained"
          disabled={ticketTypeFieldGroups.filter(group => group.active)?.some(group => !group.repeatedGroupId && group.fields?.some(field => field.required && !field.value))}
          onClick={() => changeActiveGroup(setTicketTypeFieldGroups,
            () => setInitialTicketHtmlHelper(setOpenTicketMessageEditer, selectedTicketType, ticketTypeFieldGroups, setSubject, setNewUsers, setTicketHtml, user, true))}
          sx={{ width: '50%' }}
        >
          Next
        </Button>}
        {confirmedCategoryFacilityAndType && ticketHtml && <Tooltip title={ticketMessageSubmitErrorMessage(ticketHtml, attachments, subject, autoSignature)} arrow>
          <Box sx={{ width: '50%' }}>
            <LoadingButton
              variant="contained"
              onClick={() => submitTicket(ticketHtml, selectedTicketType, ticketTypeFieldGroups, subject, selectedFacility, selectedCategory, attachments, newUsers, handleClose,
                setSubmittingTicket, navigate)}
              sx={{ width: '100%' }}
              disabled={!categoryFacilityAndTypeSet || isTestTicket || !!ticketMessageSubmitErrorMessage(ticketHtml, attachments, subject, autoSignature)}
              loading={submittingTicket}
            >
              Submit ticket
            </LoadingButton>
          </Box>
        </Tooltip>}
      </DialogActions>
    </>);
}
